import i18next from 'i18next'
import React from 'react'
import { Link } from 'widgets-for-react'
import { ComponentMountCycle, Updater } from 'widgets-for-react'
import { dataLayerServices } from '../../../seo/datalayer'
import { CMSMenuItem } from '../../cmsPage.state'
import { State } from '../../cmsPage.widget'
import { publicSiteHeaderMenu1LevelWidget } from './cmsSiteHeader.widget'
import { addWindowEvents, removeWindowEvents } from '../../utils/windowEvents'
import { DisplayType } from './cmsSiteHeader.state'
import { getTargetBlankProps } from '../../utils/link'

const t = i18next.getFixedT(null, "Header")

export type CMSHeaderLayoutStyle = {
  header: string
  headerInner: string
  nav: string
  navInner: string
  navList: string
  navOpen: string
  logo: string
  logoLabel: string
  logoImage: string
  toggler: string
  togglerLabel: string
  togglerIcon: string
  togglerLine: string
}

export const CMSHeaderLayoutClasses: CMSHeaderLayoutStyle = {
  header: 'siteheader sh',
  headerInner: 'sh__inner ls-container--l ls-gutter',
  nav: 'sh__nav',
  navInner: 'sh__nav-inner',
  navList: 'sh__list text--m',
  navOpen: 'menu--open',
  logo: 'sh__logo',
  logoLabel: 'sr-text',
  logoImage: 'sh__logo-image',
  toggler: 'sh__extra-link',
  togglerLabel: 'sh__extra-linklabel',
  togglerIcon: 'sh__extra-linkicon',
  togglerLine: '',
}

let previousScroll = 0;
let scrollDirection = 'down';

const handleHeaderScroll = () => {
  const currentScroll = window.scrollY
  const headerEl = document.querySelector('#a11y-header')

  if(headerEl == null) return;

  const refLinkEl = document.querySelector('.ref-link')
  const headerOffset = (refLinkEl == null) ? 40 : refLinkEl.clientHeight;
  const headerHeight = headerEl.clientHeight;

  if(currentScroll > previousScroll) {
    scrollDirection = 'down'
    headerEl.classList.remove('sticky');
  } else {
    scrollDirection = 'up'
  }

  if(currentScroll <= headerOffset) {
    headerEl.classList.remove('fixed');
    headerEl.classList.remove('sticky');
    document.documentElement.classList.remove('fixed--offset');
  } else if(currentScroll > (headerHeight + headerOffset)) {
    headerEl.classList.add('fixed');
    document.documentElement.classList.add('fixed--offset');

    if(scrollDirection == 'up') {
      headerEl.classList.add('sticky');
    }
  }

  previousScroll = currentScroll
}

export const CMSHeaderLayout = {
  WindowEvents: (resizer: ResizeObserver, escapeCallback: () => void) => (
    <ComponentMountCycle
      onMount={() => {
        window.addEventListener('scroll', handleHeaderScroll),
        resizer.observe(document.querySelector('#a11y-header') as Element),
        addWindowEvents({type: 'header', callback: escapeCallback})}
      }
      onUnmount={() => {
        window.removeEventListener('scroll', handleHeaderScroll),
        resizer.unobserve(document.querySelector('#a11y-header') as Element),
        removeWindowEvents({type: 'header', callback: escapeCallback})}
      }
    />
  ),
  Header: (innerHtml: JSX.Element, hideRefLink: boolean = false) => (
    <>
      <a className="skip-link" href="#a11y-main">{t("SkipToMain")}</a>
      <a className="skip-link" href="#a11y-footer">{t("SkipToFooter")}</a>

      {!hideRefLink &&
        <div className="ref-link">
          <div className="ref-link__inner ls-container--l ls-gutter text--styled">
            <p>{t("ReumaLinkText")}{' '}<a href={t("ReumaLink")} target='_blank' {...getTargetBlankProps(t("ReumaLink"))}>{t("ReumaLinkLabel")}</a></p>
          </div>
        </div>
      }

      <header id="a11y-header" className={CMSHeaderLayoutClasses.header}>
        <div className={CMSHeaderLayoutClasses.headerInner}>
          <nav className={CMSHeaderLayoutClasses.nav} aria-label={t('LabelA11Y')}>
            {innerHtml}
          </nav>
        </div>
      </header>
    </>
  ),
  NavTop: (innerHtml: JSX.Element) => (
    <div className={CMSHeaderLayoutClasses.navInner}>{innerHtml}</div>
  ),
  NavUtilityDesktop: (innerHtml: JSX.Element) => (
    <div id="a11y-topnav-desk" className="sh__topnav--desk">
      <ul className="sh__topnav-list">{innerHtml}</ul>
    </div>
  ),
  NavUtilityMobile: (innerHtml: JSX.Element) => (
    <ul className="sh__topnav-list">{innerHtml}</ul>
  ),
  NavDesktop: (innerHtml: JSX.Element) => (
    <div id="a11y-nav-desk" className="sh__main--desk">{innerHtml}</div>
  ),
  NavMobile: (innerHtml: JSX.Element) => (
    <div id="a11y-nav-mob" className="sh__main--mob">{innerHtml}</div>
  ),
  NavMain: (innerHtml: JSX.Element) => (
    <ul className={CMSHeaderLayoutClasses.navList}>{innerHtml}</ul>
  ),
  NavExtraDesktop: (innerHtml: JSX.Element) => (
    <ul className="sh__extra sh__extra--desk">{innerHtml}</ul>
  ),
  NavExtraMobile: (innerHtml: JSX.Element) => (
    <div id="a11y-extra-mob" className="sh__extra--mob">
      <ul className="sh__extra">{innerHtml}</ul>
    </div>
  ),
  NavExtraOverlayMobile: (innerHtml: JSX.Element) => (
    <ul className="sh__extra sh__extra--overlay">{innerHtml}</ul>
  ),
  NavOverlayMobile: (innerHtml: JSX.Element, isMenuOpen: boolean) => (
    <div className='sh__overlay sh__mobmenu' hidden={!isMenuOpen} aria-hidden={!isMenuOpen}>
      <div className='sh__overlay-inner sh__mobmenu-inner'>{innerHtml}</div>
    </div>
  ),
  Logo: (props: { onClick: () => void }) => (
    <Link
      onClick={props.onClick}
      to={'/'}
      title={t("ReturnHomeA11Y")}
      className={CMSHeaderLayoutClasses.logo}
      onFocus={props.onClick}
    >
      <span className={CMSHeaderLayoutClasses.logoLabel}>{t("ReturnHomeA11Y")}</span>
      <img loading="eager" fetchpriority="high" alt="Logo ReumaNederland" className={CMSHeaderLayoutClasses.logoImage} width="228" height="43" src="/images/spa/logo--100.svg" aria-hidden="true" />
    </Link>
  ),
  Toggler: (props: { isMenuOpen: boolean; onToggle: () => void }) => (
    <li className="sh__extra-item sh__extra-item--menu">
      <button
        className={`${CMSHeaderLayoutClasses.toggler} ${props.isMenuOpen === true ? 'active' : ''}`}
        aria-controls="a11y-nav-mob"
        aria-expanded={props.isMenuOpen}
        onClick={() => {
          dataLayerServices.hamburgerMenuDataLayer(props.isMenuOpen ? 'close' : 'open')
          props.onToggle()
          }}
      >
        <svg className={CMSHeaderLayoutClasses.togglerIcon} width="24px" height="24px" aria-hidden="true"><use xlinkHref={props.isMenuOpen ? "#icon--close" : "#icon--menu"} /></svg>
      </button>
    </li>
  ),
  primaryMenuItems: (props: { setState: (_: Updater<State>) => void; currentState: State; items: CMSMenuItem[], displayType: DisplayType }) =>
    publicSiteHeaderMenu1LevelWidget(props.currentState, props.items, props.displayType).run(props.setState),
}
